/**
 * UI State Store
 * Zustand store for managing UI state.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UIState {
  // Sidebar state
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;

  // Theme state (for future light mode support)
  theme: 'dark' | 'light';
  setTheme: (theme: 'dark' | 'light') => void;

  // Date range filter
  dateRange: {
    startDate: string;
    endDate: string;
  };
  setDateRange: (startDate: string, endDate: string) => void;
  setCurrentMonth: () => void;

  // Modal state
  activeModal: string | null;
  openModal: (modalId: string) => void;
  closeModal: () => void;

  // Toast notifications
  toasts: Array<{
    id: string;
    type: 'success' | 'error' | 'info';
    message: string;
  }>;
  addToast: (type: 'success' | 'error' | 'info', message: string) => void;
  removeToast: (id: string) => void;
}

const getDefaultDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  // Use UTC dates to avoid timezone issues
  // Start of month at UTC midnight
  const startDate = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));

  // End of month at UTC 23:59:59.999
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const endDate = new Date(Date.UTC(year, month, lastDay, 23, 59, 59, 999));

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Sidebar
      sidebarOpen: true,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      // Theme
      theme: 'dark',
      setTheme: (theme) => set({ theme }),

      // Date range
      dateRange: getDefaultDateRange(),
      setDateRange: (startDate, endDate) =>
        set({ dateRange: { startDate, endDate } }),
      setCurrentMonth: () => set({ dateRange: getDefaultDateRange() }),

      // Modal
      activeModal: null,
      openModal: (modalId) => set({ activeModal: modalId }),
      closeModal: () => set({ activeModal: null }),

      // Toasts
      toasts: [],
      addToast: (type, message) =>
        set((state) => ({
          toasts: [
            ...state.toasts,
            { id: `toast_${Date.now()}`, type, message },
          ],
        })),
      removeToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        })),
    }),
    {
      name: 'financeflow-ui',
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        theme: state.theme,
      }),
    }
  )
);
